import { Box, Stack } from "@mui/material";
import { DamsAppAsset } from "../../assets/registry";
import { MainPageBase } from "../page-bases/MainPageBase";
import { useEffect, useState } from "react";
import DamsTable from "../../components/Table/CollapsibleTable";
import { NoItemContent } from "../page-bases/NoItemContent";
import { TRowItem } from "../../components/Table/TableRow";
import { ModalBase } from "../../components/Modals/ModalBase";
import ModalItemInput from "../../components/Input/ModalItemInput";
import { programmesApi } from "../../services/settings/programmes";
import { DivisionSelectInput } from "../../common-ui/select-input-models/DivisionSelectInput";
import { Field, FormField } from "../../utils/formSchema";
import { PageActions, IPagination, IQueryPerams, IProgramme } from "../../config/types";
import { usePageModels } from "../../hooks/usePageActions";
import useInputUpdateHelper from "../../hooks/useInputHelper";
import { useFieldHasError } from "../../hooks/useFieldHasError";
import { isFormFilled } from "../../utils/validators";
import ActionModal from "../../components/Modals/ActionModal";
import { usePagination } from "../../hooks/usePagination";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { BulkDeleteController } from "../../common-ui/BulkDeleteController";
import { ExportModal } from "../../components/Modals/ExportModal";
import { appConfig } from "../../config/config";
import { ProgrammeLogsFilter } from "../../common-ui/page-filters/ProgrammeLogsFilter";
import { formatDate } from "../../utils/common";

interface ProgrammeTy extends FormField<{ name: string; division_id: string }> {
  submitAttempt?: boolean;
}

interface FiltersState {
  search?: string;
  programme_name?: string;
  division_name?: string;
  is_deleted: boolean;
}

const __DEFAULT_VALUE: ProgrammeTy = {
  name: { isValid: false, value: "", errorMessage: "required" },
  division_id: { isValid: false, value: "", errorMessage: "required" }
};

function ProgramsMainPage() {
  const [pageState, setPageState] = useState("main");
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const [pageAction, updatePageAction] = useState<PageActions>();
  const { models, closeModel, openModel } = usePageModels();

  const [currentFilters, setCurrentFilters] = useState<FiltersState>({
    is_deleted: false
  });

  const [showExportModal, setShowExportModal] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const [form, updateForm] = useState<ProgrammeTy>(__DEFAULT_VALUE);
  const handleInputUpdate = useInputUpdateHelper<ProgrammeTy>(updateForm);
  const fieldHasError = useFieldHasError<ProgrammeTy>(form);
  const [isProcessing, setProcess] = useState(false);

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState
  } = usePagination<IProgramme[]>(programmesApi.getProgrammes);
  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems
  } = useMangeTableItems(dataset);

  // Add querySet state for filter integration
  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });

  // Update populateDataSet function to format dates like in FacilitiesMainPage
  const populateDataSet = (data: IProgramme[], pagination?: IPagination) => {
    const formatData = data.map((data) => ({
      ...data,
      created_at: formatDate(data.created_at),
      updated_at: data.updated_at ? formatDate(data.updated_at) : 'Not Yet'
    })); 
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  const fetchData = async (query: IQueryPerams = {}) => {
    try {
      const result = await programmesApi.getProgrammes({...query, is_deleted: false });
      if (result?.data) {
        populateDataSet(result.data, result.pagination);
      }
    } catch (err) {
      setPageState("empty-db");
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleAddRequest() {
    updateForm((prev) => ({ ...prev, submitAttempt: true }));
    const formValid = isFormFilled<ProgrammeTy>(form);
    if (formValid) {
      setProcess(true);
      const request =
        pageAction?.type === "add"
          ? programmesApi.createProgramme({
              name: form.name.value,
              division_id: form.division_id.value
            })
          : programmesApi.updateProgramme(pageAction?.id ?? "", {
              name: form.name.value,
              division_id: form.division_id.value
            });
      const res = await request;
      if (res.success) {
        fetchData(querySet);
      }
      setProcess(false);
      updatePageAction({
        type: pageAction?.type,
        message: res.message,
        title: pageAction?.type === "edit" ? "Update Program" : "New Program",
        success: res.success
      });
      openModel("alert");
      updateForm(__DEFAULT_VALUE);
    }
  }

  function addNewItem() {
    updateForm(__DEFAULT_VALUE);
    openModel("add");
    updatePageAction({
      type: "add"
    });
  }

  function openDeleteModel(item: TRowItem) {
    openModel("delete");
    updatePageAction({
      id: item.id,
      name: item.name
    });
  }

  function updateField(item: TRowItem) {
    updateForm({
      name: {
        value: item.name,
        isValid: true
      },
      division_id: {
        value: item.division_id,
        isValid: true
      }
    });
    openModel("add");
    updatePageAction({
      id: item.id,
      name: item.name,
      type: "edit"
    });
  }

  async function handleDelete() {
    setProcess(true);
    const res = await programmesApi.deleteProgramme(pageAction?.id ?? "");
    if (res.success) {
      fetchData(querySet);
    }
    setProcess(false);
    updatePageAction({
      title: res.success ? "Delete Program" : "Delete Program Failed",
      message: res.message,
      success: res.success
    });
    openModel("alert");
  }

  function toggleAlertModal() {
    if (pageAction?.type === "add") {
      openModel("add");
    } else {
      closeModel();
    }
  }

  function handleDivisionSelect(item: Field) {
    updateForm((p) => ({ ...p, division_id: item }));
  }

  function clearSearch() {
    setPageState("empty-db");
  }

  function openExportModal() {
    setShowExportModal(true);
  }

  function closeExportModal() {
    setShowExportModal(false);
  }

  async function handleExportCSV() {
    setIsExportingCSV(true);
    try {
      const response = await programmesApi.exportProgrammes({
        file_format: 'csv',
        ...currentFilters,
        ...querySet
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split('/').pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace('/api/v1', '')}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: 'GET'
          });

          if (!downloadResponse.ok) {
            throw new Error('Download failed');
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || 'programmes_export.csv';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "CSV file has been downloaded successfully.",
            success: true
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, '_blank');

          updatePageAction({
            title: "Export Initiated",
            message: "CSV export has been initiated. Check your downloads folder.",
            success: true
          });
        }

        openModel("alert");
      } else {
        throw new Error('Invalid response from export API');
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "CSV export failed. Please try again.",
        success: false
      });
      openModel("alert");
    } finally {
      setIsExportingCSV(false);
      closeExportModal();
    }
  }

  async function handleExportPDF() {
    setIsExportingPDF(true);
    try {
      const response = await programmesApi.exportProgrammes({
        file_format: 'pdf',
        ...currentFilters,
        ...querySet
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split('/').pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace('/api/v1', '')}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: 'GET'
          });

          if (!downloadResponse.ok) {
            throw new Error('Download failed');
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || 'programmes_export.pdf';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "PDF file has been downloaded successfully.",
            success: true
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, '_blank');

          updatePageAction({
            title: "Export Initiated",
            message: "PDF export has been initiated. Check your downloads folder.",
            success: true
          });
        }

        openModel("alert");
      } else {
        throw new Error('Invalid response from export API');
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "PDF export failed. Please try again.",
        success: false
      });
      openModel("alert");
    } finally {
      setIsExportingPDF(false);
      closeExportModal();
    }
  }

  const renderHeaderDropDownMenu = ({closeDialog}: { closeDialog: () => void}) => {
    const batchDeleteCallback = async () => {
      resetSelectedRowItems();
      await fetchData(querySet);
      closeDialog();
    };
    return (
      <Stack direction={"column"} alignItems={"flex-start"} spacing={1}>
        <BulkDeleteController
          selectedRowItems={selectedRowItems}
          reset={batchDeleteCallback}
          ftn={programmesApi.deleteProgramme}
          idKey={"id"}
        />
      </Stack>
    );
  };

  return (
    <MainPageBase
      title={"Programmes"}
      subtitle={""}
      pageState={pageState}
      addItemBtnOnClick={addNewItem}
      addItemBtnText="Add Programme"
      exportBtnOnClick={openExportModal}
      exportBtnText={"Export"}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={pageState !== "main" ? "center" : ""}
        justifyContent={"center"}
      >
        {pageState !== "main" ? (
          <NoItemContent
            type={pageState}
            title={""}
            subtitle={""}
            addItemBtn={addNewItem}
            clearSearchBtn={clearSearch}
          />
        ) : (
          <Stack width={"100%"} spacing={1}>
            {/* Add the ProgrammeLogsFilter component here */}
            <ProgrammeLogsFilter<IProgramme[]>
              ftn={programmesApi.getProgrammes}
              updateDataSet={populateDataSet}
              updateQuerySet={setQuerySet}
            />
            
            <DamsTable
              tableTitle="Programmes"
              tableSubtitle=""
              numTableRecords="25"
              tableHeaders={columns}
              tableKeys={tkeys}
              data={dataset}
              maxRowCells={6}
              onTableItemEdit={updateField}
              onTableItemDelete={openDeleteModel}
              hasPagination
              paginationState={pagination}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
              useTableCheckbox
              onTableItemSelect={toggleRow}
              toggleSelectAllRow={toggleSelectAllRow}
              selectedRowItems={selectedRowItems}
              headerDropDownMenu={renderHeaderDropDownMenu}
              tableTitleBadge={selectedRowItems.length===0?undefined:selectedRowItems.length + ' selected' }
            />

            {/* Export Modal */}
            <ExportModal
              show={showExportModal}
              onClose={closeExportModal}
              onExportCSV={handleExportCSV}
              onExportPDF={handleExportPDF}
              isExportingCSV={isExportingCSV}
              isExportingPDF={isExportingPDF}
              title="Export Data"
              subtitle="Choose your preferred format to export programmes data"
            />

            {/* Add Program Modal */}
            <ModalBase
              open={models.showAddModel}
              title={
                pageAction?.type === "add" ? "Add New Program" : "Edit Program"
              }
              subtitle={""}
              type={"add-item"}
              showHeader={true}
              leftIcon={DamsAppAsset.icons.Programmes}
              leftBtnTxt="Cancel"
              onLeftBtnPress={closeModel}
              rightBtnTxt={
                pageAction?.type === "add" ? "Add Program" : "Edit Program"
              }
              onRightBtnPress={handleAddRequest}
              onRightBtnLoading={isProcessing}
              onClose={closeModel}
            >
              <Stack gap={2}>
                <DivisionSelectInput
                  placeholder="Select division"
                  label="Division"
                  handleChange={handleDivisionSelect}
                  value={form.division_id.value}
                  error={!form.division_id.isValid && form.submitAttempt}
                />
                <ModalItemInput
                  label="Program"
                  placeholder="Enter Program Name"
                  handleChange={handleInputUpdate("name")}
                  value={form.name.value}
                  error={fieldHasError("name")}
                  errorMessage={form.name.errorMessage}
                />
              </Stack>
            </ModalBase>

            <ModalBase
              open={models.showDeleteModel}
              title={"Delete Program"}
              subtitle={
                <Box>
                  Once you delete{" "}
                  <span style={{ fontWeight: "bold" }}>{pageAction?.name}</span>
                  , all related information will be lost forever. Are you sure
                  you want to proceed?
                </Box>
              }
              type={"action"}
              showHeader={true}
              leftIcon={DamsAppAsset.icons.delete_icon}
              leftBtnTxt="Cancel"
              onLeftBtnPress={closeModel}
              rightBtnTxt="Delete"
              onRightBtnPress={handleDelete}
              onRightBtnLoading={isProcessing}
              onClose={closeModel}
            />

            <ActionModal
              show={models.showAlertModel}
              title={pageAction?.title ?? ""}
              leftIcon={
                pageAction?.success === false
                  ? DamsAppAsset.icons.warning
                  : undefined
              }
              subtitle={pageAction?.message ?? ""}
              onLeftBtnPress={closeModel}
              leftBtnTxt={"close"}
              onRightBtnPress={toggleAlertModal}
              rightBtnTxt={pageAction?.type === "add" ? "Add New" : "continue"}
              onClosePress={closeModel}
            />
          </Stack>
        )}
      </Box>
    </MainPageBase>
  );
}

const tkeys = ["name", "division", "created_at"];
const columns = ["Programme Name", "Division", "Date Added"];

export default ProgramsMainPage;